import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showReset, setShowReset] = useState(false);
  const { login, resetPassword } = useAuth();
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await login(email, password);
      navigate('/search');
    } catch (error) {
      const msg = error.code === 'auth/invalid-credential' ? 'Invalid email or password' :
                  error.code === 'auth/too-many-requests' ? 'Too many attempts. Try again later.' :
                  'Login failed. Please try again.';
      toast.error(msg);
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error('Enter your email address first');
      return;
    }
    try {
      await resetPassword(email);
      toast.success('Password reset email sent!');
      setShowReset(false);
    } catch (error) {
      toast.error('Could not send reset email');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-navy-500 via-navy to-navy-700 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <img src="/understudy_icon.png" alt="Understudy" className="h-16 w-16 mx-auto mb-3" />
          <h1 className="font-heading text-3xl text-white mb-1">Understudy</h1>
          <p className="text-teal text-sm tracking-wide">BUSINESS CLASSIFICATION</p>
        </div>

        {/* Login Card */}
        <div className="bg-white rounded-2xl shadow-2xl p-8">
          <h2 className="font-heading text-xl text-navy text-center mb-6">
            {showReset ? 'Reset Password' : 'Sign In'}
          </h2>

          <form onSubmit={showReset ? handleReset : handleLogin}>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="input-field"
                  placeholder="you@company.com"
                  required
                  autoFocus
                />
              </div>

              {!showReset && (
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">Password</label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="input-field pr-12"
                      placeholder="••••••••"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPassword ? '🙈' : '👁️'}
                    </button>
                  </div>
                </div>
              )}

              <button
                type="submit"
                disabled={isLoading}
                className="btn-primary w-full flex items-center justify-center gap-2"
              >
                {isLoading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    <span>Signing in...</span>
                  </>
                ) : (
                  showReset ? 'Send Reset Email' : 'Sign In'
                )}
              </button>
            </div>
          </form>

          <div className="mt-4 text-center">
            <button
              onClick={() => setShowReset(!showReset)}
              className="text-sm text-teal hover:text-teal-600 font-medium"
            >
              {showReset ? '← Back to Sign In' : 'Forgot password?'}
            </button>
          </div>
        </div>

        <p className="text-center text-navy-200 text-xs mt-6">
          © {new Date().getFullYear()} Understudy • classify.myunderstudy.ai
        </p>
      </div>
    </div>
  );
}
